import apiClient from '../apiClient';
import type { ApiResponse, EmailLog, EmailStats, PaginationMeta, EmailType, EmailStatus } from '@/types';
import { getUrlWithQueryParams } from '@/lib/utils';
import { useQuery, useMutation } from '@tanstack/react-query';
import { ReactQueryKeys } from '../reactQueryKeys';

const BASE_URL = '/admin/emails';

interface PaginatedResponse {
  items: EmailLog[];
  pagination: PaginationMeta;
}

export const getEmails = async (
  page?: number,
  perPage?: number,
  search?: string,
  type?: EmailType,
  status?: EmailStatus,
): Promise<PaginatedResponse> => {
  const url = getUrlWithQueryParams(BASE_URL, {
    page,
    perPage,
    search,
    type,
    status,
  });
  const { data } = await apiClient.get<ApiResponse<EmailLog[]>>(url);
  return {
    items: data.data,
    pagination: data.meta!,
  };
};

export const getEmail = async (id: string): Promise<EmailLog> => {
  const { data } = await apiClient.get<ApiResponse<EmailLog>>(`${BASE_URL}/${id}`);
  return data.data;
};

export const getEmailStats = async (): Promise<EmailStats> => {
  const { data } = await apiClient.get<ApiResponse<EmailStats>>(`${BASE_URL}/stats`);
  return data.data;
};

export const sendTestEmail = async (type: string, params: { email: string; name?: string; lang?: string }): Promise<EmailLog> => {
  const url = getUrlWithQueryParams(`${BASE_URL}/test/${type}`, params);
  const { data } = await apiClient.post<ApiResponse<EmailLog>>(url);
  return data.data;
};

// React Query Hooks

export const useGetEmails = (
  page?: number,
  perPage?: number,
  search?: string,
  type?: EmailType,
  status?: EmailStatus,
) =>
  useQuery({
    queryKey: [ReactQueryKeys.GET_EMAILS, page, perPage, search, type, status],
    queryFn: () => getEmails(page, perPage, search, type, status),
  });

export const useGetEmail = (id: string) =>
  useQuery({
    queryKey: [ReactQueryKeys.GET_EMAIL, id],
    queryFn: () => getEmail(id),
    enabled: !!id,
  });

export const useGetEmailStats = () =>
  useQuery({
    queryKey: [ReactQueryKeys.GET_EMAIL_STATS],
    queryFn: getEmailStats,
  });

export const useSendTestEmail = () =>
  useMutation({
    mutationFn: ({ type, params }: { type: string; params: { email: string; name?: string; lang?: string } }) =>
      sendTestEmail(type, params),
  });
